import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createAudioContext, resumeAudioContext, createSpeakerRoute } from '../audioContext';

// m:ss countdown for the doctor ETA.
function formatClock(totalSeconds) {
  const s = Math.max(0, Math.round(totalSeconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// Countdown-ring circumference (r=25). Precomputed so the dash maths reads
// as one value rather than being recalculated inline on every tick.
const RING_C = 2 * Math.PI * 25;

// Initials for the on-call doctor's monogram. Drops honorifics so
// "Dr. Sarah Mitchell" gives SM rather than DS.
function clinicianInitials(name) {
  const parts = String(name || '')
    // Longest-first, plus a trailing \b: with 'prof' ahead of 'professor'
    // the alternation matched the prefix and left "essor" as the first name.
    .replace(/\b(?:professor|prof|doctor|dr|mrs|miss|mr|ms)\b\.?\s*/gi, '')
    .trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'DR';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

// Coarse minutes for flight ETA — a to-the-second countdown on a 40-minute
// flight reads as false precision.
function formatEtaMinutes(totalSeconds) {
  const mins = Math.ceil(Math.max(0, totalSeconds) / 60);
  if (mins <= 1) return 'under a minute';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function PatientJoin() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const streamingRef = useRef(false);
  const rafRef = useRef(null);
  const recorderRef = useRef(null);

  const params = new URLSearchParams(window.location.search);
  const roomId = params.get('room') || 'default';
  // Token from the clinician's SMS link. When present it replaces the shared
  // room password — the patient taps through straight into the call.
  const inviteToken = params.get('t') || '';
  const inviteTokenRef = useRef(inviteToken);

  const [status, setStatus] = useState('connecting');
  const [error, setError] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [incomingMuted, setIncomingMuted] = useState(false); // Mute clinician/MATILDA audio
  const [cameraFacing, setCameraFacing] = useState('user'); // 'user' (front) or 'environment' (back)
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');
  // 'none' = no link in the URL (password flow); 'checking' | 'valid' | 'invalid'
  const [linkStatus, setLinkStatus] = useState(inviteToken ? 'checking' : 'none');
  const [linkError, setLinkError] = useState('');
  const [connectingDoctor, setConnectingDoctor] = useState(false);
  // Escalation: named on-call doctor + a countdown to their expected arrival.
  const [escalation, setEscalation] = useState(null); // { clinician, urgency }
  const [etaRemaining, setEtaRemaining] = useState(null); // seconds, counts down
  // The ETA we started from, so the countdown ring has a denominator. Captured
  // on first notice and NOT re-set by reconnect replays, which send the
  // remaining time — using that as the total would reset the ring to full.
  const [etaTotal, setEtaTotal] = useState(null);
  // Aeromedical retrieval progress, once the clinician tasks an aircraft.
  const [flight, setFlight] = useState(null);
  // Set when the clinician changed the patient's mic, so we can say so plainly.
  // 'muted' | 'unmuted' | null
  const [micNotice, setMicNotice] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [hasLeft, setHasLeft] = useState(false);
  // The clinician ended the consult (as opposed to the patient leaving).
  const [sessionEnded, setSessionEnded] = useState(false);
  // Bumped on rejoin to re-run the connect effect without sending the patient
  // back through the password screen.
  const [sessionKey, setSessionKey] = useState(0);
  // Set before we close the socket ourselves so ws.onclose knows not to
  // auto-reconnect — otherwise leaving just bounces the patient back in 2s.
  const leftRef = useRef(false);
  const rejoiningRef = useRef(false);
  const incomingMutedRef = useRef(false);
  const cameraFacingRef = useRef('user');
  const audioMutedRef = useRef(false);
  const videoOffRef = useRef(false);
  const passwordRef = useRef('');
  const speakerAudioRef = useRef(null);
  const speakerRouteRef = useRef(createSpeakerRoute('patient'));
  const matildaSpeakingRef = useRef(false);
  // Dedicated <audio> element for MATILDA TTS. Uses a blob URL with WAV-wrapped PCM
  // so playback bypasses the MediaStreamDestination → MediaStream pipeline (iOS
  // applies adaptive playback rate to MediaStreams, which produced mid-utterance
  // speed wobble). Playing a static WAV file matches /api/tts-test exactly.
  const matildaAudioRef = useRef(null);
  const matildaAudioUrlRef = useRef(null); // current blob URL — revoked when next plays
  // Pending sentence chunks, and whether one is mid-playback.
  const matildaQueueRef = useRef([]);
  const matildaPlayingRef = useRef(false);

  // Audio capture refs
  const audioContextRef = useRef(null);
  const pcm16NodeRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioSourceRef = useRef(null);

  // Audio playback refs (for clinician audio)
  const playbackCtxRef = useRef(null);
  const playbackQueueRef = useRef([]);
  const playbackPlayingRef = useRef(false);
  const playbackNextTimeRef = useRef(0);

  const startAudioCapture = useCallback(async (stream) => {
    try {
      const audioCtx = createAudioContext({ sampleRate: 24000 });
      if (!audioCtx) return;
      await audioCtx.audioWorklet.addModule('/pcm16-worklet.js');
      const source = audioCtx.createMediaStreamSource(stream);
      const pcm16Node = new AudioWorkletNode(audioCtx, 'pcm16-downsampler');

      pcm16Node.port.onmessage = (event) => {
        const pcm16Buffer = event.data;
        if (pcm16Buffer && pcm16Buffer.byteLength > 0 && !audioMutedRef.current) {
          const ws = wsRef.current;
          if (ws && ws.readyState === WebSocket.OPEN) {
            const bytes = new Uint8Array(pcm16Buffer);
            const b64 = btoa(String.fromCharCode(...bytes));
            ws.send(JSON.stringify({ type: 'audio_chunk', speaker: 'patient', data: b64 }));
          }
        }
      };

      source.connect(pcm16Node);
      audioContextRef.current = audioCtx;
      pcm16NodeRef.current = pcm16Node;
      audioSourceRef.current = source;
      audioStreamRef.current = stream;
    } catch (err) {
      console.error('Audio capture init failed:', err);
    }
  }, []);

  const stopAudioCapture = useCallback(() => {
    if (pcm16NodeRef.current) { pcm16NodeRef.current.port.close(); pcm16NodeRef.current = null; }
    if (audioSourceRef.current) { audioSourceRef.current.disconnect(); audioSourceRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null; }
  }, []);

  // Release the camera and microphone hardware. Tearing down the AudioContext
  // graph is not enough — without an explicit track.stop() the device stays
  // live (camera indicator on) after the patient leaves.
  const stopAllMedia = useCallback(() => {
    const streams = [videoRef.current?.srcObject, audioStreamRef.current];
    streams.forEach(stream => {
      if (!stream || typeof stream.getTracks !== 'function') return;
      stream.getTracks().forEach(track => { try { track.stop(); } catch {} });
    });
    if (videoRef.current) videoRef.current.srcObject = null;
    audioStreamRef.current = null;
    setCameraActive(false);
  }, []);

  // Wrap raw PCM16 mono samples in a WAV header so the audio can be played by a
  // standard <audio> element. 44-byte RIFF header.
  const pcmToWavBytes = (pcmBytes, sampleRate) => {
    const channels = 1;
    const bitsPerSample = 16;
    const byteRate = sampleRate * channels * (bitsPerSample / 8);
    const blockAlign = channels * (bitsPerSample / 8);
    const dataSize = pcmBytes.length;
    const buf = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buf);
    // "RIFF"
    view.setUint8(0, 0x52); view.setUint8(1, 0x49); view.setUint8(2, 0x46); view.setUint8(3, 0x46);
    view.setUint32(4, 36 + dataSize, true);
    // "WAVE"
    view.setUint8(8, 0x57); view.setUint8(9, 0x41); view.setUint8(10, 0x56); view.setUint8(11, 0x45);
    // "fmt "
    view.setUint8(12, 0x66); view.setUint8(13, 0x6d); view.setUint8(14, 0x74); view.setUint8(15, 0x20);
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, channels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    // "data"
    view.setUint8(36, 0x64); view.setUint8(37, 0x61); view.setUint8(38, 0x74); view.setUint8(39, 0x61);
    view.setUint32(40, dataSize, true);
    new Uint8Array(buf, 44).set(pcmBytes);
    return new Uint8Array(buf);
  };

  // Play MATILDA TTS via dedicated <audio> element with WAV blob URL. Avoids
  // iOS Safari's adaptive-rate playback on MediaStream-routed audio. Plays
  // through device speaker (same path as a static <audio> file).
  // MATILDA's reply now arrives as one chunk per sentence so the first can play
  // while the rest are still being synthesised. That means these must QUEUE:
  // assigning el.src per chunk (as this used to) makes each one cut off the last,
  // so only the final sentence would be heard.
  const playNextMatildaChunk = useCallback(() => {
    const el = matildaAudioRef.current;
    if (!el || matildaPlayingRef.current) return;
    const next = matildaQueueRef.current.shift();
    if (!next) return;

    matildaPlayingRef.current = true;
    if (matildaAudioUrlRef.current) {
      try { URL.revokeObjectURL(matildaAudioUrlRef.current); } catch {}
    }
    const url = URL.createObjectURL(next);
    matildaAudioUrlRef.current = url;
    el.muted = incomingMutedRef.current;
    el.src = url;
    el.play().catch(err => {
      console.error('[MATILDA audio] play() failed:', err);
      // Don't strand the queue on a single failed chunk.
      matildaPlayingRef.current = false;
      playNextMatildaChunk();
    });
  }, []);

  const playMatildaAudio = useCallback((base64, sampleRate = 24000) => {
    try {
      const binary = atob(base64);
      const pcmBytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) pcmBytes[i] = binary.charCodeAt(i);
      if (pcmBytes.length === 0) return;
      const wav = pcmToWavBytes(pcmBytes, sampleRate);
      matildaQueueRef.current.push(new Blob([wav], { type: 'audio/wav' }));
      playNextMatildaChunk();
    } catch (err) {
      console.error('[MATILDA audio] playMatildaAudio error:', err);
    }
  }, [playNextMatildaChunk]);

  const stopMatildaAudio = useCallback(() => {
    // Clear the queue as well as the current chunk: on a barge-in the
    // remaining sentences are already in the browser and would otherwise
    // keep playing after MATILDA was supposed to stop.
    matildaQueueRef.current = [];
    matildaPlayingRef.current = false;
    const el = matildaAudioRef.current;
    if (el) {
      try { el.pause(); el.currentTime = 0; el.removeAttribute('src'); el.load?.(); } catch {}
    }
    if (matildaAudioUrlRef.current) {
      try { URL.revokeObjectURL(matildaAudioUrlRef.current); } catch {}
      matildaAudioUrlRef.current = null;
    }
  }, []);

  // Shared with the on-call view (audioContext.js): arms once, and if the
  // phone refuses play() it falls back to the context output instead of
  // routing every chunk into a stream nothing is playing.
  const ensureSpeakerRoute = useCallback(() => {
    speakerRouteRef.current.arm(playbackCtxRef.current, speakerAudioRef.current);
  }, []);

  const playPcm16 = useCallback((base64, sampleRate = 24000) => {
    // 24kHz playback context matches what server sends for both MATILDA TTS and the mic
    // relay path. Avoids any browser-side resampling edge cases that were producing speed
    // wobble on iOS. MATILDA TTS now arrives as ONE buffer per utterance, played by a
    // single BufferSourceNode — no chunked scheduling drift.
    if (!playbackCtxRef.current) {
      playbackCtxRef.current = createAudioContext({ sampleRate: 24000 });
    }
    const ctx = playbackCtxRef.current;
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    // Always route through loudspeaker (esp. iOS, which defaults to earpiece for AudioContext output).
    ensureSpeakerRoute();

    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const numSamples = bytes.length / 2;
      if (numSamples === 0) return;
      const buffer = ctx.createBuffer(1, numSamples, sampleRate);
      const ch = buffer.getChannelData(0);
      for (let i = 0; i < bytes.length; i += 2) {
        const sample = bytes[i] | (bytes[i + 1] << 8);
        ch[i / 2] = sample > 32767 ? (sample - 65536) / 32768 : sample / 32768;
      }

      const src = ctx.createBufferSource();
      src.buffer = buffer;
      // ONE output only, via the route's bus: loudspeaker when armed, the
      // context output otherwise. Connecting to both produced overlapping
      // playback through two parallel pipelines.
      src.connect(speakerRouteRef.current.output(ctx));

      const now = ctx.currentTime;
      if (playbackNextTimeRef.current < now) {
        playbackNextTimeRef.current = now + 0.02;
      }
      src.start(playbackNextTimeRef.current);
      playbackNextTimeRef.current += buffer.duration;
      // Track scheduled sources so we can flush them on cancel
      if (!playbackQueueRef.current) playbackQueueRef.current = [];
      playbackQueueRef.current.push(src);
      src.onended = () => {
        const q = playbackQueueRef.current;
        if (q) {
          const i = q.indexOf(src);
          if (i >= 0) q.splice(i, 1);
        }
      };
    } catch (err) {
      console.error('Audio playback error:', err);
    }
  }, []);

  const flushPlayback = useCallback(() => {
    const q = playbackQueueRef.current;
    if (q) {
      q.forEach(src => { try { src.stop(); src.disconnect(); } catch {} });
      playbackQueueRef.current = [];
    }
    if (playbackCtxRef.current) {
      playbackNextTimeRef.current = playbackCtxRef.current.currentTime + 0.02;
    }
  }, []);

  // Full playback teardown. The speaker route MUST be reset alongside the
  // context — a MediaStreamDestination belonging to a closed AudioContext is
  // dead, and playPcm16 would keep routing into it after a rejoin.
  const teardownPlayback = useCallback(() => {
    const q = playbackQueueRef.current;
    if (q) {
      q.forEach(src => { try { src.stop(); src.disconnect(); } catch {} });
      playbackQueueRef.current = [];
    }
    playbackNextTimeRef.current = 0;
    speakerRouteRef.current.reset(speakerAudioRef.current);
    if (playbackCtxRef.current) {
      try { playbackCtxRef.current.close(); } catch {}
      playbackCtxRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: cameraFacingRef.current, width: { ideal: 480 }, height: { ideal: 360 } },
        audio: {
          // Explicitly requested. MATILDA's TTS plays out of the patient's own
          // speaker and is picked up by their mic, then forwarded to transcription
          // as patient speech — so without echo cancellation her own words land in
          // the transcript attributed to the patient, and then poison her LLM
          // context on the next turn. Suppressing the mic during her turn is NOT
          // an option: barge-in is driven by speech_started from that same
          // transcription stream, so the audio has to keep flowing.
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 24000,
          channelCount: 1
        }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraActive(true);
      startAudioCapture(stream);
      return true;
    } catch (err) {
      setError('Camera access denied. Please allow camera and microphone permissions and reload.');
      return false;
    }
  }, [startAudioCapture]);

  // Swap front/back camera without disturbing the audio capture pipeline.
  const flipCamera = useCallback(async () => {
    const next = cameraFacingRef.current === 'user' ? 'environment' : 'user';
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: next }, width: { ideal: 480 }, height: { ideal: 360 } }
      });
      const newVideoTrack = videoStream.getVideoTracks()[0];
      if (!newVideoTrack) return;

      const currentStream = videoRef.current?.srcObject;
      if (currentStream) {
        currentStream.getVideoTracks().forEach(t => { try { t.stop(); } catch {} currentStream.removeTrack(t); });
        currentStream.addTrack(newVideoTrack);
      } else if (videoRef.current) {
        videoRef.current.srcObject = videoStream;
      }
      cameraFacingRef.current = next;
      setCameraFacing(next);
    } catch (err) {
      console.error('Camera flip failed:', err);
    }
  }, []);

  const startStreaming = useCallback(() => {
    streamingRef.current = true;
    const FPS_INTERVAL = 40; // 25 fps target
    const SEND_BUFFER_LIMIT = 64 * 1024; // 64 KiB — allow ~one frame in flight

    const captureAndSend = () => {
      if (!streamingRef.current) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.videoWidth > 0 && !videoOffRef.current && wsRef.current?.readyState === WebSocket.OPEN && wsRef.current.bufferedAmount < SEND_BUFFER_LIMIT) {
        const ctx = canvas.getContext('2d');
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        const scale = Math.min(480 / vw, 480 / vh);
        canvas.width = Math.round(vw * scale);
        canvas.height = Math.round(vh * scale);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob && wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(blob);
          }
        }, 'image/jpeg', 0.35);
      }
      rafRef.current = setTimeout(captureAndSend, FPS_INTERVAL);
    };
    rafRef.current = setTimeout(captureAndSend, FPS_INTERVAL);
  }, []);

  // Single owner of the patient's mic state, whoever initiated the change.
  // Always reports the resulting state back so the clinician's console shows
  // what is actually true rather than the last command it sent.
  const applyMicMuted = useCallback((next, by) => {
    setAudioMuted(next);
    audioMutedRef.current = next;
    if (by === 'clinician') {
      setMicNotice(next ? 'muted' : 'unmuted');
    } else {
      // The patient overriding a clinician mute clears the notice — they have
      // seen it and acted on it.
      setMicNotice(null);
    }
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: 'patient_mic_state', muted: next, by }));
      } catch {}
    }
  }, []);

  // Validate an SMS invite link before showing the join screen, so an expired
  // link says so plainly instead of failing at the WebSocket handshake.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch('/api/patient-link/check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: inviteToken, room: roomId })
        });
        if (cancelled) return;
        if (resp.ok) {
          setLinkStatus('valid');
        } else {
          const data = await resp.json().catch(() => ({}));
          setLinkStatus('invalid');
          setLinkError(data.reason === 'expired'
            ? 'This link has expired.'
            : 'This link is no longer valid.');
        }
      } catch {
        if (!cancelled) {
          setLinkStatus('invalid');
          setLinkError('Could not check your link. Please check your connection.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [inviteToken, roomId]);

  // Count the escalation ETA down locally. The server re-bases it on reconnect,
  // so drift never accumulates across a dropout. Gated on a boolean rather than
  // the value itself so we hold one interval instead of rebuilding it every tick.
  const etaCountingDown = etaRemaining != null && etaRemaining > 0;
  useEffect(() => {
    if (!etaCountingDown) return;
    const timer = setInterval(() => {
      setEtaRemaining(prev => (prev == null ? null : Math.max(0, prev - 1)));
    }, 1000);
    return () => clearInterval(timer);
  }, [etaCountingDown]);

  useEffect(() => {
    if (!authenticated) return;
    let mounted = true;

    async function init() {
      // On a reconnect the camera and audio graph are usually still live, so
      // reuse them. Re-acquiring unconditionally leaked a MediaStream and an
      // AudioContext per reconnect, and browsers cap concurrent AudioContexts —
      // after a handful of drops on a poor connection, `new AudioContext()`
      // starts throwing and mic capture dies silently while video carries on.
      // Reusing also avoids a camera flicker and a repeat permission prompt.
      const tracks = videoRef.current?.srcObject?.getTracks?.() || [];
      const captureAlive =
        tracks.length > 0 &&
        tracks.some(t => t.readyState === 'live') &&
        !!pcm16NodeRef.current &&
        audioContextRef.current &&
        audioContextRef.current.state !== 'closed';

      if (!captureAlive) {
        // Release whatever is left before re-acquiring, so a half-dead graph
        // doesn't accumulate either.
        stopAudioCapture();
        stopAllMedia();
        const cameraOk = await startCamera();
        if (!cameraOk || !mounted) return;
      }

      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsPort = window.location.port === '3000' ? '5001' : window.location.port;
      const wsHost = `${wsProtocol}//${window.location.hostname}${wsPort ? ':' + wsPort : ''}`;
      const ws = new WebSocket(wsHost);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'auth-error') {
            setAuthenticated(false);
            setAuthError(msg.message || 'Invalid password');
            setStatus('connecting');
            ws.close();
          }
          if (msg.type === 'matilda_speaking') {
            matildaSpeakingRef.current = msg.speaking;
            if (msg.flush) {
              flushPlayback();
              stopMatildaAudio();
            }
          }
          if (msg.type === 'connecting_doctor') {
            setConnectingDoctor(!!msg.connecting);
          }
          if (msg.type === 'session_ended') {
            // Reuse the leave teardown: stop streaming, release camera and mic,
            // and stop reconnecting. The patient should not be left with a live
            // camera once the consult is over.
            leftRef.current = true;
            streamingRef.current = false;
            if (rafRef.current) { clearTimeout(rafRef.current); rafRef.current = null; }
            flushPlayback();
            stopMatildaAudio();
            stopAudioCapture();
            stopAllMedia();
            setConnectingDoctor(false);
            setSessionEnded(true);
            setHasLeft(true);
            setStatus('left');
            try { ws.close(); } catch {}
            return;
          }
          if (msg.type === 'mic_directive') {
            applyMicMuted(!!msg.muted, 'clinician');
          }
          if (msg.type === 'escalation_notice') {
            setEscalation({
              clinician: msg.clinician || null,
              urgency: msg.urgency || 'urgent'
            });
            if (typeof msg.etaSeconds === 'number') {
              setEtaRemaining(msg.etaSeconds);
              if (!msg.replay) setEtaTotal(msg.etaSeconds);
              else setEtaTotal(prev => (prev == null ? msg.etaSeconds : prev));
            }
          }
          if (msg.type === 'flight_dispatched' || msg.type === 'flight_update') {
            setFlight(msg.flight || null);
          }
          if (msg.type === 'session_reset') {
            // Clinician started a new session — flush any buffered audio and
            // clear our local indicators. WebSocket stays open so the patient
            // doesn't have to re-join.
            flushPlayback();
            stopMatildaAudio();
            setConnectingDoctor(false);
            setEscalation(null);
            setEtaRemaining(null);
            setEtaTotal(null);
            setFlight(null);
            setMicNotice(null);
            matildaSpeakingRef.current = false;
          }
          if (msg.type === 'audio_chunk' && (msg.speaker === 'clinician' || msg.speaker === 'matilda')) {
            if (!incomingMutedRef.current) {
              if (msg.speaker === 'matilda') {
                // MATILDA TTS arrives as a single full-utterance buffer — play via WAV
                // blob URL on a dedicated <audio> element (smooth playback on iOS).
                playMatildaAudio(msg.data, msg.sampleRate || 24000);
              } else {
                // Clinician mic relay is real-time chunks — keep AudioContext path.
                playPcm16(msg.data, msg.sampleRate || 24000);
              }
            }
          }
        } catch {}
      };

      ws.onopen = () => {
        ws.send(JSON.stringify({
          type: 'join',
          role: 'patient',
          room: roomId,
          // Invite token wins when present; the server accepts either.
          inviteToken: inviteTokenRef.current || undefined,
          password: passwordRef.current
        }));
        // Report current mic state on (re)connect so the console never shows a
        // stale toggle after a dropout.
        ws.send(JSON.stringify({
          type: 'patient_mic_state',
          muted: audioMutedRef.current,
          by: 'patient'
        }));
        setStatus('live');
        startStreaming();
      };

      ws.onerror = () => {
        if (!leftRef.current) setError('Connection failed. Please check your internet and try again.');
      };
      ws.onclose = () => {
        streamingRef.current = false;
        // Deliberate leave — do not reconnect. leaveCall owns the UI state.
        if (leftRef.current) return;
        if (mounted) {
          setStatus('disconnected');
          setTimeout(() => { if (mounted && !leftRef.current) init(); }, 2000);
        }
      };
    }

    init();

    return () => {
      mounted = false;
      streamingRef.current = false;
      if (rafRef.current) clearTimeout(rafRef.current);
      if (wsRef.current) wsRef.current.close();
      stopAudioCapture();
      stopAllMedia();
      // On a rejoin the playback context was just created in the user's tap —
      // closing it here would silence MATILDA for the rest of the call.
      if (rejoiningRef.current) {
        rejoiningRef.current = false;
      } else {
        teardownPlayback();
      }
    };
  }, [roomId, startCamera, startStreaming, authenticated, sessionKey, stopAudioCapture, stopAllMedia, teardownPlayback]);

  // Patient-initiated exit. Ends *their participation* only — the clinician
  // keeps the transcript, escalation and assessment, and only they can start a
  // new session.
  const leaveCall = useCallback(() => {
    leftRef.current = true;
    setConfirmLeave(false);
    streamingRef.current = false;
    if (rafRef.current) { clearTimeout(rafRef.current); rafRef.current = null; }

    // Tell the server this was intentional before the socket goes away, so the
    // clinician sees "ended the call" rather than a dropout.
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'leave', role: 'patient', room: roomId })); } catch {}
    }
    if (ws) { try { ws.close(); } catch {} }
    wsRef.current = null;

    stopMatildaAudio();
    teardownPlayback();
    stopAudioCapture();
    stopAllMedia();

    setConnectingDoctor(false);
    setError(null);
    setStatus('left');
    setHasLeft(true);
  }, [roomId, stopMatildaAudio, teardownPlayback, stopAudioCapture, stopAllMedia]);

  // Recover from an accidental leave without re-entering the room password.
  const rejoin = useCallback(() => {
    leftRef.current = false;
    setSessionEnded(false);
    // The sessionKey bump below re-runs the connect effect, whose cleanup would
    // otherwise close the AudioContext we are about to create in this gesture.
    rejoiningRef.current = true;
    setHasLeft(false);
    setError(null);
    setStatus('connecting');
    // Re-create playback inside the user gesture so mobile autoplay policy
    // lets MATILDA speak again after the rejoin.
    if (!playbackCtxRef.current) {
      try {
        const ctx = createAudioContext({ sampleRate: 24000 });
        if (!ctx) return;
        resumeAudioContext(ctx);
        playbackCtxRef.current = ctx;
      } catch {}
    }
    // A tap is the one place a previously refused loudspeaker route may succeed.
    speakerRouteRef.current.allowRetry();
    speakerRouteRef.current.arm(playbackCtxRef.current, speakerAudioRef.current);
    if (matildaAudioRef.current) {
      try {
        const silent = pcmToWavBytes(new Uint8Array(48), 24000);
        const url = URL.createObjectURL(new Blob([silent], { type: 'audio/wav' }));
        matildaAudioRef.current.src = url;
        matildaAudioRef.current.play().then(() => {
          matildaAudioRef.current.pause();
          URL.revokeObjectURL(url);
        }).catch(() => { try { URL.revokeObjectURL(url); } catch {} });
      } catch {}
    }
    setSessionKey(k => k + 1);
  }, []);

  // Everything that must happen inside the joining tap: create the playback
  // AudioContext and unlock the MATILDA <audio> element, both of which mobile
  // browsers only permit during a user gesture. Shared by the password form and
  // the SMS-link button so neither path can silently skip the unlock.
  const beginSession = useCallback(() => {
    if (!playbackCtxRef.current) {
      try {
        const ctx = createAudioContext({ sampleRate: 24000 });
        if (!ctx) return;
        resumeAudioContext(ctx);
        playbackCtxRef.current = ctx;
      } catch {}
    }
    // A tap is the one place a previously refused loudspeaker route may succeed.
    speakerRouteRef.current.allowRetry();
    speakerRouteRef.current.arm(playbackCtxRef.current, speakerAudioRef.current);
    if (matildaAudioRef.current) {
      try {
        const silent = pcmToWavBytes(new Uint8Array(48), 24000); // ~1ms of silence
        const url = URL.createObjectURL(new Blob([silent], { type: 'audio/wav' }));
        matildaAudioRef.current.src = url;
        matildaAudioRef.current.play().then(() => {
          matildaAudioRef.current.pause();
          URL.revokeObjectURL(url);
        }).catch(() => { try { URL.revokeObjectURL(url); } catch {} });
      } catch {}
    }
    setAuthenticated(true);
  }, []);

  // Clamped once: the route trail and the aircraft glyph must use the exact
  // same value or the plane drifts off the end of its own trail.
  const flightProgress = Math.min(1, Math.max(0, flight?.progress || 0));

  const joinShellStyle = {
    minHeight: '100vh', background: '#151521', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const statusConfig = {
    connecting: { text: 'Connecting...', color: '#6c757d', pulse: true },
    live: { text: 'Streaming — Live', color: '#28a745', pulse: false },
    disconnected: { text: 'Reconnecting...', color: '#dc3545', pulse: true },
    left: { text: 'Call ended', color: '#6c757d', pulse: false }
  };

  const current = statusConfig[status] || statusConfig.connecting;

  if (!authenticated) {
    // An expired or tampered SMS link gets a plain dead end rather than the
    // password form — offering the password here would train patients to expect
    // a shared secret, which is the thing the link is meant to replace.
    if (linkStatus === 'invalid') {
      return (
        <div style={joinShellStyle}>
          <img src="/logos/rfds-logo.png" alt="RFDS" style={{ height: '48px', width: 'auto', marginBottom: '16px' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
          <h1 style={{ color: '#fff', fontSize: '22px', fontWeight: '600', margin: '0 0 10px' }}>Link no longer valid</h1>
          <p style={{ color: '#888', fontSize: '14px', textAlign: 'center', maxWidth: '320px', lineHeight: '1.5' }}>
            {linkError} Please contact your clinician for a new link.
          </p>
          <p style={{ color: '#666', fontSize: '13px', marginTop: '24px' }}>If you need urgent help, call 000.</p>
          <img src="/logos/uncapt-logo-white.svg" alt="UNCAPT" style={{ height: '14px', width: 'auto', marginTop: '44px' }} />
        </div>
      );
    }

    if (linkStatus === 'checking') {
      return (
        <div style={joinShellStyle}>
          <img src="/logos/rfds-logo.png" alt="RFDS" style={{ height: '48px', width: 'auto', marginBottom: '16px' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
          <p style={{ color: '#888', fontSize: '14px', animation: 'pulse 1.6s infinite' }}>Checking your link…</p>
          <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
        </div>
      );
    }

    const viaLink = linkStatus === 'valid';
    return (
      <div style={joinShellStyle}>
        <img src="/logos/rfds-logo.png" alt="RFDS" style={{ height: '48px', width: 'auto', marginBottom: '16px' }}
          onError={(e) => { e.target.style.display = 'none'; }} />
        <h1 style={{ color: '#fff', fontSize: '24px', fontWeight: '600', margin: '0 0 6px' }}>MATILDA</h1>
        <p style={{ color: '#888', fontSize: '14px', marginBottom: '28px', textAlign: 'center', maxWidth: '300px', lineHeight: '1.5' }}>
          {viaLink
            ? 'Your clinician is waiting. Tap below to start your assessment.'
            : 'Enter the room password to join'}
        </p>
        <form onSubmit={(e) => {
          e.preventDefault();
          // The link flow has no password to check; the token was already
          // validated above. Both paths still need this tap: it is the user
          // gesture that unlocks audio playback under mobile autoplay policy.
          if (!viaLink && !password.trim()) return;
          if (!viaLink) passwordRef.current = password.trim();
          setAuthError('');
          beginSession();
        }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', width: '100%', maxWidth: '300px' }}>
          {!viaLink && (
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Room password"
              autoFocus
              style={{
                width: '100%', padding: '14px 16px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)',
                background: '#1e1e2d', color: '#fff', fontSize: '16px', outline: 'none', textAlign: 'center',
                boxSizing: 'border-box'
              }}
            />
          )}
          <button type="submit" style={{
            width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
            background: '#2563eb', color: '#fff', fontSize: '16px', fontWeight: '600', cursor: 'pointer'
          }}>{viaLink ? 'Start assessment' : 'Join Session'}</button>
        </form>
        <p style={{ color: '#666', fontSize: '12px', marginTop: '14px', textAlign: 'center', maxWidth: '280px', lineHeight: '1.5' }}>
          You'll be asked to allow camera and microphone access.
        </p>
        {authError && (
          <p style={{ color: '#ff6b7a', fontSize: '14px', marginTop: '12px' }}>{authError}</p>
        )}
        <img src="/logos/uncapt-logo-white.svg" alt="UNCAPT" style={{ height: '14px', width: 'auto', marginTop: '48px' }} />
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#151521',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <img src="/logos/rfds-logo.png" alt="RFDS" style={{ height: '40px', width: 'auto', marginBottom: '12px' }}
          onError={(e) => { e.target.style.display = 'none'; }} />
        <h1 style={{ color: '#fff', fontSize: '24px', fontWeight: '600', margin: 0 }}>MATILDA</h1>
        <p style={{ color: '#888', fontSize: '14px', marginTop: '4px' }}>Remote Telehealth Assessment</p>
      </div>

      <div style={{
        position: 'relative', width: '100%', maxWidth: '400px',
        borderRadius: '16px', overflow: 'hidden', background: '#1e1e2d',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
      }}>
        <video ref={videoRef} autoPlay playsInline muted
          style={{ width: '100%', height: 'auto', display: cameraActive ? 'block' : 'none', transform: 'scaleX(-1)' }}
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        {!cameraActive && (
          <div style={{ width: '100%', paddingTop: '75%', position: 'relative' }}>
            <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#666' }}>
              Starting camera...
            </span>
          </div>
        )}
        <div style={{
          position: 'absolute', top: '12px', left: '12px', display: 'flex', alignItems: 'center', gap: '8px',
          background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', borderRadius: '20px', padding: '6px 14px'
        }}>
          <div style={{
            width: '8px', height: '8px', borderRadius: '50%', background: current.color,
            animation: current.pulse ? 'pulse 2s infinite' : 'none'
          }} />
          <span style={{ color: '#fff', fontSize: '12px', fontWeight: '500' }}>{current.text}</span>
        </div>
        {connectingDoctor && (
          <div style={{
            position: 'absolute', top: '12px', right: '12px', display: 'flex', alignItems: 'center', gap: '8px',
            background: 'rgba(59,130,246,0.18)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(59,130,246,0.5)', borderRadius: '20px', padding: '6px 14px'
          }}>
            <div style={{
              width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6',
              animation: 'pulse 1.4s infinite'
            }} />
            <span style={{ color: '#fff', fontSize: '12px', fontWeight: '500' }}>Connecting your doctor…</span>
          </div>
        )}
      </div>

      {/* Clinician changed the patient's mic. Shown explicitly, with the fact
          that they can undo it — a silently muted patient in a clinical
          assessment may be trying to report something and not know why nobody
          is responding. */}
      {micNotice && status === 'live' && (
        <div style={{
          marginTop: '16px', width: '100%', maxWidth: '400px', boxSizing: 'border-box',
          display: 'flex', alignItems: 'flex-start', gap: '10px',
          padding: '12px 15px', borderRadius: '12px',
          background: micNotice === 'muted' ? 'rgba(220,53,69,0.12)' : 'rgba(22,163,74,0.1)',
          border: `1px solid ${micNotice === 'muted' ? 'rgba(220,53,69,0.45)' : 'rgba(22,163,74,0.4)'}`
        }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
            stroke={micNotice === 'muted' ? '#ff8d99' : '#6ee7a8'}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, marginTop: '1px' }}>
            {micNotice === 'muted' ? (
              <>
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                <path d="M17 16.95A7 7 0 0 1 5 12v-2" />
              </>
            ) : (
              <>
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              </>
            )}
          </svg>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: '13px', fontWeight: '600' }}>
              {micNotice === 'muted'
                ? 'Your clinician muted your microphone'
                : 'Your clinician turned your microphone back on'}
            </div>
            <div style={{ color: '#9aa0ae', fontSize: '12px', marginTop: '2px', lineHeight: '1.45' }}>
              {micNotice === 'muted'
                ? 'They can’t hear you right now. Tap the microphone button below to turn it back on whenever you need to speak.'
                : 'They can hear you again.'}
            </div>
          </div>
        </div>
      )}

      {/* Escalation. Same surface, radius and shadow as the video card above so
          it reads as the next card in a stack rather than a panel bolted on.
          The countdown is the hero element — a draining ring gives the wait a
          shape, which is the whole job of this card. */}
      {escalation && (
        <div style={{
          marginTop: '16px', width: '100%', maxWidth: '400px', boxSizing: 'border-box',
          borderRadius: '16px', overflow: 'hidden', background: '#1e1e2d',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          animation: 'pjRise 0.45s cubic-bezier(0.16, 1, 0.3, 1) both'
        }}>
          {/* Hairline accent — the only saturated colour on the surface */}
          <div style={{ height: '2px', background: 'linear-gradient(90deg, #2563eb, #60a5fa 55%, rgba(96,165,250,0))' }} />

          <div style={{ padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
              {/* Doctor monogram — reads as a person, not an alert icon */}
              <div style={{
                width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(145deg, #2f4d8f, #1e3a72)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#dbeafe', fontSize: '15px', fontWeight: '600', letterSpacing: '0.5px',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.12)'
              }}>{clinicianInitials(escalation.clinician?.name)}</div>

              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  color: '#7f96c4', fontSize: '10px', fontWeight: '700',
                  letterSpacing: '0.9px', textTransform: 'uppercase', marginBottom: '3px'
                }}>
                  <span style={{
                    width: '5px', height: '5px', borderRadius: '50%', background: '#60a5fa',
                    animation: 'pjBreathe 2.4s ease-in-out infinite'
                  }} />
                  Doctor joining
                </div>
                <div style={{
                  color: '#fff', fontSize: '16px', fontWeight: '600',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>{escalation.clinician?.name || 'On-call doctor'}</div>
                {escalation.clinician?.specialty && (
                  <div style={{
                    color: '#8b93a7', fontSize: '12px', marginTop: '1px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>{escalation.clinician.specialty}</div>
                )}
              </div>

              {/* Countdown ring */}
              {etaRemaining != null && etaRemaining > 0 && (
                <div style={{ position: 'relative', width: '58px', height: '58px', flexShrink: 0 }}>
                  <svg width="58" height="58" viewBox="0 0 58 58" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="29" cy="29" r="25" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
                    <circle
                      cx="29" cy="29" r="25" fill="none" stroke="#60a5fa" strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={RING_C}
                      strokeDashoffset={RING_C * (1 - (etaTotal ? Math.min(1, etaRemaining / etaTotal) : 1))}
                      style={{ transition: 'stroke-dashoffset 1s linear' }}
                    />
                  </svg>
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center'
                  }}>
                    <span style={{
                      color: '#fff', fontSize: '14px', fontWeight: '700',
                      fontVariantNumeric: 'tabular-nums', lineHeight: 1
                    }}>{formatClock(etaRemaining)}</span>
                    <span style={{ color: '#6b7590', fontSize: '8px', letterSpacing: '0.5px', marginTop: '2px' }}>MINS</span>
                  </div>
                </div>
              )}
              {etaRemaining === 0 && (
                <span style={{
                  flexShrink: 0, padding: '6px 11px', borderRadius: '999px',
                  background: 'rgba(96,165,250,0.16)', color: '#93c5fd',
                  fontSize: '11px', fontWeight: '600'
                }}>Now</span>
              )}
            </div>

            <p style={{
              color: '#9aa3b8', fontSize: '13px', lineHeight: '1.55',
              margin: '15px 0 0', paddingTop: '14px',
              borderTop: '1px solid rgba(255,255,255,0.06)'
            }}>
              MATILDA has shared your assessment with them. Stay on the call — you
              don&rsquo;t need to do anything.
            </p>
          </div>
        </div>
      )}

      {/* Aeromedical retrieval. The route line replaces a progress bar: it shows
          where the aircraft is between two named points, which is what the
          patient actually wants to know. */}
      {flight && (
        <div style={{
          marginTop: '14px', width: '100%', maxWidth: '400px', boxSizing: 'border-box',
          borderRadius: '16px', overflow: 'hidden', background: '#1e1e2d',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          animation: 'pjRise 0.45s cubic-bezier(0.16, 1, 0.3, 1) both'
        }}>
          <div style={{ height: '2px', background: 'linear-gradient(90deg, #16a34a, #6ee7a8 55%, rgba(110,231,168,0))' }} />

          <div style={{ padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  color: '#5f9e78', fontSize: '10px', fontWeight: '700',
                  letterSpacing: '0.9px', textTransform: 'uppercase', marginBottom: '3px'
                }}>Aeromedical retrieval</div>
                <div style={{ color: '#fff', fontSize: '16px', fontWeight: '600' }}>
                  {flight.phase === 'arrived' ? 'Aircraft has arrived'
                    : flight.phase === 'enroute' ? 'Aircraft on the way'
                    : 'Aircraft being prepared'}
                </div>
              </div>
              {flight.etaSeconds > 0 && (
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{
                    color: '#6ee7a8', fontSize: '19px', fontWeight: '700',
                    fontVariantNumeric: 'tabular-nums', lineHeight: 1.1
                  }}>{formatEtaMinutes(flight.etaSeconds)}</div>
                  <div style={{ color: '#6b7590', fontSize: '10px', letterSpacing: '0.4px' }}>ETA</div>
                </div>
              )}
            </div>

            {/* Route: solid = flown, dashed = remaining, aircraft at the join */}
            <div style={{ position: 'relative', height: '30px', margin: '18px 0 2px' }}>
              <div style={{
                position: 'absolute', left: '4px', right: '4px', top: '50%', height: '2px',
                transform: 'translateY(-50%)', borderRadius: '1px',
                background: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.22) 0 4px, transparent 4px 9px)'
              }} />
              <div style={{
                position: 'absolute', left: '4px', top: '50%', height: '2px',
                width: `calc((100% - 8px) * ${flightProgress})`,
                transform: 'translateY(-50%)', borderRadius: '1px',
                background: 'linear-gradient(90deg, #16a34a, #6ee7a8)',
                transition: 'width 1s linear'
              }} />
              {/* Origin + destination nodes */}
              <span style={{
                position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                width: '8px', height: '8px', borderRadius: '50%', background: '#6ee7a8'
              }} />
              <span style={{
                position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
                width: '8px', height: '8px', borderRadius: '50%',
                background: flight.phase === 'arrived' ? '#6ee7a8' : 'rgba(255,255,255,0.22)',
                boxShadow: flight.phase === 'arrived' ? '0 0 0 4px rgba(110,231,168,0.18)' : 'none'
              }} />
              {/* Aircraft glyph, riding the route */}
              <span style={{
                position: 'absolute', top: '50%',
                left: `calc(4px + (100% - 8px) * ${flightProgress})`,
                transform: 'translate(-50%, -50%)',
                transition: 'left 1s linear',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '26px', height: '26px', borderRadius: '50%',
                background: '#1e1e2d', border: '1px solid rgba(110,231,168,0.5)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.5)'
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="#6ee7a8"
                  style={{ transform: 'rotate(45deg)' }}>
                  <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" />
                </svg>
              </span>
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              color: '#6b7590', fontSize: '11px', marginTop: '10px',
              paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.06)'
            }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {flight.origin}
              </span>
              <span style={{ flexShrink: 0, marginLeft: '10px', color: '#8b93a7' }}>
                {flight.remainingKm > 0 ? `${flight.remainingKm} km away` : 'On scene'}
              </span>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div style={{
          marginTop: '20px', padding: '12px 20px', background: 'rgba(220,53,69,0.1)',
          border: '1px solid rgba(220,53,69,0.3)', borderRadius: '8px', color: '#ff6b7a',
          fontSize: '14px', maxWidth: '400px', textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      {status === 'live' && (
        <div style={{ display: 'flex', gap: '14px', marginTop: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {/* Mute all incoming audio (clinician + MATILDA) */}
          <button
            onClick={() => {
              const next = !incomingMuted;
              setIncomingMuted(next);
              incomingMutedRef.current = next;
              if (next) { flushPlayback(); stopMatildaAudio(); }
              if (speakerAudioRef.current) speakerAudioRef.current.muted = next;
              if (matildaAudioRef.current) matildaAudioRef.current.muted = next;
            }}
            title={incomingMuted ? 'Unmute audio' : 'Mute audio'}
            style={{
              width: '56px', height: '56px', borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: incomingMuted ? '#dc3545' : 'rgba(255,255,255,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              {incomingMuted ? (
                <>
                  <line x1="23" y1="9" x2="17" y2="15" />
                  <line x1="17" y1="9" x2="23" y2="15" />
                </>
              ) : (
                <>
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                </>
              )}
            </svg>
          </button>
          {/* Microphone (your voice). Always usable — a clinician directive
              changes this state but never takes the control away. */}
          <button
            onClick={() => applyMicMuted(!audioMuted, 'patient')}
            title={audioMuted ? 'Unmute mic' : 'Mute mic'}
            style={{
              width: '56px', height: '56px', borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: audioMuted ? '#dc3545' : 'rgba(255,255,255,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {audioMuted ? (
                <>
                  <line x1="1" y1="1" x2="23" y2="23" />
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                  <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.12 1.5-.34 2.18" />
                  <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                </>
              ) : (
                <>
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                </>
              )}
            </svg>
          </button>
          {/* Camera on/off */}
          <button
            onClick={() => {
              const next = !videoOff;
              setVideoOff(next);
              videoOffRef.current = next;
              if (videoRef.current?.srcObject) {
                videoRef.current.srcObject.getVideoTracks().forEach(t => { t.enabled = !next; });
              }
            }}
            title={videoOff ? 'Turn camera on' : 'Turn camera off'}
            style={{
              width: '56px', height: '56px', borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: videoOff ? '#dc3545' : 'rgba(255,255,255,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {videoOff ? (
                <>
                  <line x1="1" y1="1" x2="23" y2="23" />
                  <path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h4a2 2 0 0 1 2 2v9.34" />
                </>
              ) : (
                <>
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </>
              )}
            </svg>
          </button>
          {/* Flip front/back camera */}
          <button
            onClick={flipCamera}
            disabled={videoOff}
            title="Flip camera"
            style={{
              width: '56px', height: '56px', borderRadius: '50%', border: 'none',
              cursor: videoOff ? 'default' : 'pointer',
              background: 'rgba(255,255,255,0.12)',
              opacity: videoOff ? 0.4 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background 0.2s'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7h13a4 4 0 0 1 4 4v1" />
              <polyline points="6 4 3 7 6 10" />
              <path d="M21 17H8a4 4 0 0 1-4-4v-1" />
              <polyline points="18 20 21 17 18 14" />
            </svg>
          </button>
        </div>
      )}

      {/* Leave call — deliberately separated from the media toggles above and
          text-labelled, so it can't be hit while reaching for mute on a phone. */}
      {(status === 'live' || status === 'disconnected') && (
        <button
          onClick={() => setConfirmLeave(true)}
          style={{
            marginTop: '22px', padding: '13px 26px', borderRadius: '26px', cursor: 'pointer',
            background: 'rgba(220,53,69,0.14)', border: '1px solid rgba(220,53,69,0.5)',
            color: '#ff6b7a', fontSize: '15px', fontWeight: '600',
            display: 'flex', alignItems: 'center', gap: '9px'
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff6b7a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
          Leave call
        </button>
      )}

      {/* Post-leave state. Rendered as an overlay rather than an early return so
          the hidden <audio> elements stay mounted — remounting them would drop
          the iOS autoplay unlock and silence MATILDA after a rejoin. */}
      {hasLeft && (
        <div style={{
          position: 'fixed', inset: 0, background: '#151521', zIndex: 1100,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '20px', textAlign: 'center'
        }}>
          <img src="/logos/rfds-logo.png" alt="RFDS" style={{ height: '40px', width: 'auto', marginBottom: '16px' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px'
          }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8b93a7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          </div>
          <h1 style={{ color: '#fff', fontSize: '22px', fontWeight: '600', margin: '0 0 8px' }}>
            {sessionEnded ? 'Your consultation has ended' : "You've left the call"}
          </h1>
          <p style={{ color: '#888', fontSize: '14px', margin: '0 0 28px', maxWidth: '320px', lineHeight: '1.5' }}>
            {sessionEnded
              ? 'Your clinician has ended the call. Your camera and microphone are off, and your care team has everything from this assessment.'
              : 'Your camera and microphone are off. Your clinician still has everything from this assessment.'}
          </p>
          {/* No rejoin when the clinician ended it — the room's transcription is
              stopped, so reconnecting would drop them into a dead session. */}
          {!sessionEnded && (
            <button
              onClick={rejoin}
              style={{
                padding: '14px 28px', borderRadius: '10px', border: 'none', background: '#2563eb',
                color: '#fff', fontSize: '16px', fontWeight: '600', cursor: 'pointer', minWidth: '200px'
              }}
            >Rejoin call</button>
          )}
          <p style={{ color: '#666', fontSize: '13px', marginTop: '20px', maxWidth: '300px', lineHeight: '1.5' }}>
            If you need urgent help, call 000.
          </p>
          <img src="/logos/uncapt-logo-white.svg" alt="UNCAPT" style={{ height: '14px', width: 'auto', marginTop: '40px' }} />
        </div>
      )}

      {confirmLeave && (
        <div
          onClick={() => setConfirmLeave(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#1e1e2d', borderRadius: '16px', padding: '26px 24px', maxWidth: '340px',
              width: '100%', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)'
            }}
          >
            <h2 style={{ color: '#fff', fontSize: '18px', fontWeight: '600', margin: '0 0 10px' }}>
              {connectingDoctor ? 'A doctor is being connected' : 'Leave this call?'}
            </h2>
            <p style={{ color: '#9aa0ae', fontSize: '14px', margin: '0 0 22px', lineHeight: '1.5' }}>
              {connectingDoctor
                ? 'Your doctor is joining you right now. If you leave, they will not be able to see or hear you.'
                : 'Your camera and microphone will turn off. You can rejoin afterwards if you need to.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={leaveCall}
                style={{
                  padding: '14px', borderRadius: '10px', border: 'none', background: '#dc3545',
                  color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
                }}
              >{connectingDoctor ? 'Leave anyway' : 'Leave call'}</button>
              <button
                onClick={() => setConfirmLeave(false)}
                style={{
                  padding: '14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)',
                  background: 'transparent', color: '#fff', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
                }}
              >{connectingDoctor ? 'Wait for my doctor' : 'Stay on the call'}</button>
            </div>
          </div>
        </div>
      )}

      <p style={{ color: '#666', fontSize: '13px', marginTop: '16px', textAlign: 'center', maxWidth: '300px', lineHeight: '1.5' }}>
        {status === 'live' ? 'Your clinician can see you now. Stay in frame for best results.' : ''}
      </p>

      <audio ref={speakerAudioRef} autoPlay playsInline style={{ display: 'none' }} />
      {/* onEnded advances the sentence queue — without it only the first chunk
          of a multi-sentence reply would ever play. */}
      <audio
        ref={matildaAudioRef}
        playsInline
        preload="auto"
        onEnded={() => { matildaPlayingRef.current = false; playNextMatildaChunk(); }}
        onError={() => { matildaPlayingRef.current = false; playNextMatildaChunk(); }}
        style={{ display: 'none' }}
      />

      <img src="/logos/uncapt-logo-white.svg" alt="UNCAPT" style={{ height: '14px', width: 'auto', marginTop: '32px' }} />

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes pjBreathe { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.45;transform:scale(0.82)} }
        @keyframes pjRise { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:none } }
        @media (prefers-reduced-motion: reduce) {
          @keyframes pjRise { from { opacity:0 } to { opacity:1 } }
          @keyframes pjBreathe { 0%,100%{opacity:1} 50%{opacity:0.6} }
        }
      `}</style>
    </div>
  );
}

export default PatientJoin;
